import {
  createElement,
  useEffect,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
} from 'react';
import Draggable, {
  type DraggableData,
  type DraggableProps,
} from 'react-draggable';

export interface ModalOffset {
  x: number;
  y: number;
}

export interface ModalDragBounds {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export interface WrapDraggableModalOptions {
  /** 是否启用标题栏拖动。 */
  draggable?: boolean;
  /** 打开周期标识；变化时将位移复位。 */
  resetKey?: string | number;
}

const INITIAL_OFFSET: ModalOffset = { x: 0, y: 0 };
const INITIAL_BOUNDS: ModalDragBounds = {
  left: 0,
  right: 0,
  top: 0,
  bottom: 0,
};
const HORIZONTAL_VISIBLE_SIZE = 48;
const VERTICAL_VISIBLE_SIZE = 40;
const CANCEL_SELECTOR =
  '.ant-modal-close, .ant-modal-header button, .ant-modal-header a';
const DraggableComponent =
  Draggable as ComponentType<Partial<DraggableProps>>;

/** 判断事件目标是否为可起拖的 Modal 标题栏区域，并排除标题栏内的交互元素。 */
export function isModalDragHandle(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  const header = target.closest('.ant-modal-header');
  return header !== null && target.closest(CANCEL_SELECTOR) === null;
}

/** 将目标位移限制在给定边界内，避免标题栏完全离开视口。 */
export function clampModalOffset(
  offset: ModalOffset,
  bounds: ModalDragBounds,
): ModalOffset {
  return {
    x: Math.min(Math.max(offset.x, bounds.left), bounds.right),
    y: Math.min(Math.max(offset.y, bounds.top), bounds.bottom),
  };
}

function calculateBounds(
  node: HTMLElement,
  current: ModalOffset,
): ModalDragBounds | null {
  const header = node.querySelector<HTMLElement>('.ant-modal-header');
  if (!header) return null;

  const modalRect = node.getBoundingClientRect();
  const headerRect = header.getBoundingClientRect();

  return {
    left: current.x + HORIZONTAL_VISIBLE_SIZE - modalRect.right,
    right: current.x + window.innerWidth - HORIZONTAL_VISIBLE_SIZE - modalRect.left,
    top: current.y + VERTICAL_VISIBLE_SIZE - headerRect.bottom,
    bottom: current.y + window.innerHeight - VERTICAL_VISIBLE_SIZE - headerRect.top,
  };
}

interface DraggableModalNodeProps extends WrapDraggableModalOptions {
  node: ReactNode;
}

function DraggableModalNode({
  node,
  draggable = true,
  resetKey = 0,
}: DraggableModalNodeProps) {
  const nodeRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<ModalOffset>(INITIAL_OFFSET);
  const [bounds, setBounds] = useState<ModalDragBounds>(INITIAL_BOUNDS);

  useEffect(() => {
    setPosition(INITIAL_OFFSET);
    setBounds(INITIAL_BOUNDS);
  }, [resetKey]);

  if (!draggable) return node;

  const handleStart = (event: MouseEvent, data: DraggableData) => {
    if (!isModalDragHandle(event.target)) return false;
    const nextBounds = calculateBounds(data.node, position);
    if (!nextBounds) return false;
    setBounds(nextBounds);
  };

  const handleDrag = (_event: MouseEvent, data: DraggableData) => {
    setPosition(clampModalOffset({ x: data.x, y: data.y }, bounds));
  };

  return createElement(
    DraggableComponent,
    {
      bounds,
      cancel: CANCEL_SELECTOR,
      handle: '.ant-modal-header',
      nodeRef,
      onDrag: handleDrag,
      onStart: handleStart,
      position,
    },
    createElement('div', { ref: nodeRef }, node),
  );
}

/** 用统一拖动容器包装 AntD modalRender 节点，供声明式与命令式 Modal 共用。 */
export function wrapDraggableModalNode(
  node: ReactNode,
  options: WrapDraggableModalOptions = {},
): ReactNode {
  return createElement(DraggableModalNode, { ...options, node });
}
